import { Injectable } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { TLoadDependeciesParams } from '@df/catalog/model';
import { Product as CoreProduct, IProductData, IProductDataV2, IProductDataV3 } from '@df/catalog/product/product';
import { Resource } from '@df/catalog/resource';
import { StoreResource } from '@df/catalog/store/store.resource';
import { NgInject } from '@df/ng/ng-inject';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import { NICmsContentComponent } from '@icms/core/content';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import compact from 'lodash-es/compact';
import flatten from 'lodash-es/flatten';
import uniq from 'lodash-es/uniq';
import { SeeRangeInStore } from '../see-range-in-store/see-range-in-store';
import { SeeRangeInStoreService } from '../see-range-in-store/see-range-in-store.service';
import { NCmsProductDelivery } from './cms/delivery/cms-product-delivery.component';
import { ProductService } from './product.service';

// Moved here to avoid circular dependency.
export namespace NProductViewLayout {
    export const BLOCK_ID = 'cms-product-view-layout';
    export type IBlockData = Catalog.IBlockData<IBlockDataContent>;
    export interface IBlockDataContent {
        /**
         * Specification sections
         */
        attributes: string[];
        /**
         * Top components list
         */
        topComponents: NICmsContentComponent.IData[];
        /**
         * Bottom components list
         */
        bottomComponents: NICmsContentComponent.IData[];
        dimensions_icons?: IDimensionIcon[];
        dimensionsTopAttributes: IAttributeItem[];
        dimensions_sections?: IDimensionSection[];
        product: Product<IProductDataV3>;
        content: IContent;
        sale_banner_font_color?: string;
        topOutletCategoriesWhitelist?: number[];
    }
    export interface IDimensionIcon {
        image: string;
        attributes?: string[] | string;
    }
    export interface IDimensionSection {
        title: string;
        attributes?: string | string[];
    }
    export interface IAttributeItem {
        isText: boolean;
        isTextArea: boolean;
        label: string;
        valuesLabels: string[];
        code: string;
    }
    export interface IContent {
        attributes: string[];
        topComponents: NICmsContentComponent.IData[];
        bottomComponents: NICmsContentComponent.IData[];
        sale_banner_font_color?: string;
        dimensions_icons?: IDimensionIcon[];
        dimensionsTopAttributes: IAttributeItem[];
        dimensions_sections?: IDimensionSection[];
        icon_sections: string[];
        specSections: string[];
    }
}
declare module '@df/catalog/product/product' {
    interface IProductDataV2 {
        /**
         * Product color
         */
        color?: number | number[];
        /**
         * Alternative image used on listing page on hover
         */
        img_alternative?: string;
        /**
         * Horizontal image that can be used on 2-col grid
         */
        img_horizontal?: string;
        /**
         * Text under product name used on listing page for banners
         */
        banner_subtitle?: string;
        /**
         * Link that banner provides
         */
        banner_url?: string;
        banner_type?: EProductBannerType;
        range_name?: string;
        product_outline?: string;
        finance_allowed?: number | boolean;
        po_in_stock_eta?: number;
        first_po_quantity?: number;
        leadtime?: number;
        express_delivery?: 1 | 0;
        clearance?: 1 | 0;
        show_finance_popup?: 1 | 0;
        warranty?: string;
        configFamilyMap?: IProductConfigFamilyMapItem[];
        crossSellFamilyMap?: IProductCrossSellFamilyMapItem[];
        nyw_item_range_code_nh?: number;
        nyw_item_category_code_nh?: number;
        nyw_item_brand_code_nh?: number;
        web_finish?: string;
        default_delivery_method_bs?: number;
        nyw_item_brand_desc_nh_bs?: number | number[];
        nyw_item_category_desc_nh_bs?: number | number[];
        nyw_item_range_desc_nh_bs?: number | number[];
        variant_attribute_1?: number | number[];
        category_ids?: number[];
        conscious_collection?: number;
        made_in?: number;
        show_more_colour_options?: boolean;
        img_swatch?: string;
        config_colour?: number;
        banner_title?: string;
        banner_cta?: string;
        promotion_banner?: number;
        mattressprodtype?: string;
        warranty_years?: string;
        sale_banner?: number;
        in_stock_lead_time_label?: string;
        PLP_promo_banner?: number;
        PLP_promo_banner_colour?: number;
        isBanner?: boolean;
        item_status?: number;
        qty_increments?:number;
        min_sale_qty?: number;
    }
    interface IProductDataV3 {
        /**
         * Id of the product page layout (used with cms block)
         */
        product_page?: string;
        sample_fabric?: number;
        care_plan_features?: number[];
        careinfo?: boolean;
        plan_info?: string;
        isProductProtectionPlan?: boolean;
        default_variant?: 1 | 0;
        store_collect?: 1 | 0;
        config_size?: number;
        config_colour?: number;
        clearance_sold?: boolean;
        long_description?: string;
        storedepartmentcode?: EProductStoreDepartmentCode;
        nyw_item_range_code_nh?: number;
        additional_delivery_method_1?: number;
        title_product_range_block?: string;
        text_field_product_range_block?: string;
        title_product_range_block_2?: string;
        text_field_product_range_block_2?: string;
        title_product_range_block_3?: string;
        text_field_product_range_block_3?: string;
        image_product_range_block?: string;
        trust_message1: string;
        trust_message2: string;
        trust_message3: string;
        next?: boolean;
        vendorno?: number;
        attribute_set: EAttrSet;
        price_prefix?: string;
    }
}
export interface IProductConfigFamilyMapItem {
    price?: number;
    org_price?: number;
    image?: string;
    img_swatch?: string;
    url?: string;
    product_outline?: string;
    listing_label?: number;
    default_variant?: boolean;
    range_name?: string;
    name?: string;
    img_alternative?: string;
    img_horizontal?: string;
    id?: number;
    color_filter?: number;
    qty?: number;
    config_colour?: number;
}
export interface IProductCrossSellFamilyMapItem extends IProductConfigFamilyMapItem {
    backorders?: boolean;
    backorders_qty?: number;
    config_colour?: number;
    config_size?: number;
}
/**
 * Colors map entry type
 */
export interface IColorMapEntry {
    color: number;
    parent: number;
}
export enum EListingLabelOptionId {
    clearance = 5450,
    newIn = 5449,
    expressDelivery = 5498,
    lastChance = 47584,
    soldOut = 26312,
    inStock = 26311
}
export enum EProductBannerType {
    textOnImage = 5454,
    textBelowImage = 5453
}
export enum EProductStoreDepartmentCode {
    accessories = 6022,
    bedsAndBedroom = 5887,
    dingingAndLiving = 5883,
    garden = 7014,
    office = 5891,
    rugs = 5889,
    sundries = 7067,
    upholstery = 5885
}
export enum ENywItemCategoryCodeNh {
    diningChairs = 16957,
    barStools = 16981
}

export enum EAttrSet {
    assemblyPlan = 39
}
export interface IProductLabel {
    label: string;
    color?: string;
}
export class Product<T extends IProductData = IProductData> extends CoreProduct<T> {
    /**
     * Load cms block servoce
     */
    @NgInject(ICmsBlockResource)
    private iCmsBlockResource!: ICmsBlockResource;
    @NgInject(SeeRangeInStoreService)
    private seeRangeInStoreService!: SeeRangeInStoreService;
    @NgInject(StoreResource)
    private storeResource!: StoreResource;
    @NgInject(ProductService)
    private productService!: ProductService;

    protected override addDependenciesToLoader(params: TLoadDependeciesParams): void {
        super.addDependenciesToLoader(params);
        const loader = params.loader;
        if ((this.verbosity || 0) > 2 && params.dependencyLevel >= 30) {
            loader.add(
                ...[
                    [NProductViewLayout.BLOCK_ID, this.ppLayoutVariant],
                    [NCmsProductDelivery.BLOCK_ID, this.deliveryVariant]
                ].map(blockData =>
                    this.iCmsBlockResource.retrieve({ bareUrl: blockData[0] as string, variant: blockData[1] }, undefined)
                )
            );
            const ids = [...this.familySamplesIds, ...this.crossSellIds, ...this.upSellIds];
            if (ids) {
                loader.add(this.resources.product.loadAllIds(ids, 3, { dependencyLevel: 20, safe: true }));
            }
            if (this.isClearance) {
                loader.add(this.loadSeeRangeInStoreModel());
                const parentSku = this.clearanceParentSku;
                if (parentSku) {
                    loader.add(
                        new Promise<void>(resolve => {
                            const finish = () => resolve();
                            this.resources.product.findOne({ verbosity: 2, filter: { sku: parentSku } }).finally(finish);
                        })
                    );
                }
            }
        }
        if (this.verbosity === 2 && this.isBanner) {
            loader.add(this.load(3));
        }
    }
    getListingLabels(): IProductLabel[] {
        const labels: IProductLabel[] = [];
        this.getListingLabelsOptionsIds().forEach(value => {
            const label = this.productService.getListingLabelProductLabel(value);
            label && labels.push(label);
        });
        if (this.isConsciousCollection && this.consciousCollection) {
            labels.push({ label: this.consciousCollection?.toString() });
        }
        if (this.isMadeIn && this.madeIn) {
            labels.push({ label: this.madeIn?.toString() });
        }
        return labels.filter(i => i.label);
    }
    private getListingLabelsOptionsIds(): number[] {
        const ids: number[] = [];
        if (this.isOutOfStock) {
            ids.push(EListingLabelOptionId.soldOut);
        }
        if (this.isClearanceOnline) {
            ids.push(EListingLabelOptionId.lastChance);
        }
        if (this.isClearance) {
            ids.push(EListingLabelOptionId.clearance);
        }
        if (this.isExpressDelivery && this.stock?.qty && this.stock.qty > 0) {
            ids.push(EListingLabelOptionId.expressDelivery);
        }
        if (this.stock?.qty && this.stock.qty > 0) {
            ids.push(EListingLabelOptionId.inStock);
        }
        if ((<IProductDataV2>this.data).listing_label) {
            ids.push((<IProductDataV2>this.data).listing_label as number);
        }
        return ids.filter(i => !!i);
    }
    /**
     * Product page layout variant
     */
    get ppLayoutVariant(): string {
        return 'default';
    }
    get hasColorOption(): boolean {
        const data = this.data as IProductDataV2;
        return data && !data.isBanner && this.isConfigurable && !!data.options?.configurable?.['color'];
    }
    /**
     * Size guide block variant
     */
    get sizeGuideVariant(): string | undefined {
        return 'default';
    }
    /**
     * Delivery and returns guide block variant
     */
    get deliveryVariant(): string | undefined {
        return this.getAttr('default_delivery_method_bs') ? this.getAttr('default_delivery_method_bs') + '' : 'default';
    }
    get pricePrefix(): string | undefined {
        return (<IProductDataV3>this.data).price_prefix;
    }
    get isBanner(): boolean {
        if (this.data) {
            return !!(<IProductDataV2>this.data).isBanner;
        }
        return false;
    }
    /**
     * Range content block variant
     */
    get rangeContentVariant(): string {
        return this.getAttr('nyw_item_range_code_nh') ? this.getAttr('nyw_item_range_code_nh') + '' : 'default';
    }
    get familySamplesIds(): number[] {
        return uniq(flatten(this.family.map(i => (<Product<IProductDataV3>>i).sampleIds).filter(i => !!i))) || [];
    }
    get sampleProducts(): Product<IProductDataV3>[] {
        const products = this.resources.product.getAllExisting(this.sampleIds) as Product<IProductDataV3>[];
        return products.filter(product => product.isInStock());
    }
    get sampleIds(): number[] {
        const data = this.data as IProductDataV2;
        return compact(flatten([data?.sample]));
    }
    get familySamples(): Product[] {
        return compact(
            this.family.flatMap(i => {
                return (<Product<IProductDataV3>>i).sampleProducts;
            }) as Product[]
        );
    }
    get isClearance(): boolean {
        if (this.data) {
            const data = this.data as IProductDataV2;
            return !!data.clearance;
        }
        return false;
    }
    get isClearanceSold(): boolean {
        if (this.data && this.isClearance) {
            const data = this.data as IProductDataV3;
            return !!data.clearance_sold;
        }
        return false;
    }
    get isClearanceOnline(): boolean {
        if (this.data) {
            const data = this.data as IProductDataV2;
            return !!data.category_ids?.includes(505);
        }
        return false;
    }
    get isConsciousCollection(): boolean {
        if (this.data) {
            const data = this.data as IProductDataV2;
            return !!data.conscious_collection;
        }
        return false;
    }
    get consciousCollection(): string | number | undefined {
        return this.getAttrLabel('conscious_collection');
    }
    get isMadeIn(): boolean {
        if (this.data) {
            const data = this.data as IProductDataV2;
            return !!data.made_in;
        }
        return false;
    }
    get madeIn(): string | number | undefined {
        return this.getAttrLabel('made_in');
    }
    get isWarrantyAndReturns(): boolean {
        if (this.data) {
            const data = this.data as IProductDataV2;
            return !!data.warranty;
        }
        return false;
    }
    get isExpressDelivery(): boolean {
        if (this.data) {
            const data = this.data as IProductDataV2;
            return !!data.express_delivery;
        }
        return false;
    }
    /**
     * Use this to set force priority attributes order
     */
    protected override getConfigurableAttributeCodesOrder() {
        return ['color'];
    }
    get isFinanceAllowed(): boolean {
        if (this.data) {
            const data = this.data as IProductDataV3;
            return (data.price || 0) >= 500;
        }
        return false;
    }
    private get upSellIds(): number[] {
        if (this.hasV3) {
            const data = this.data as IProductDataV3;
            return data?.link?.upSell || [];
        }
        return [];
    }
    private get crossSellIds(): number[] {
        if (this.hasV3) {
            const data = this.data as IProductDataV3;
            return data?.link?.crossSell || [];
        }
        return [];
    }
    get crossSellProducts(): Product<IProductDataV3>[] {
        const products = this.resources.product.getAllExisting(this.crossSellIds) as Product<IProductDataV3>[];
        return products.filter(i => i?.data?.url);
    }
    get hasSizeCrosssells(): boolean {
        if (this.isSimple && (this.data as IProductDataV3)?.config_size) {
            return !!this.sizeCrossSellProducts.length;
        }
        return false;
    }
    get sizeCrossSellProducts(): Product<IProductDataV3>[] {
        return this.crossSellProducts.filter(i => i?.data?.config_size && i.data.config_size !== (<IProductDataV3>this.data)?.config_size);
    }
    get hasColorCrosssells(): boolean {
        return this.isSimple && !!(this.data as IProductDataV3)?.config_colour && !!this.colorCrossSellProducts.length;
    }
    get colorCrossSellProducts(): Product<IProductDataV3>[] {
        return this.crossSellProducts.filter(i => !!i?.data?.config_colour && !i.isOutOfStock);
    }
    get seeRangeInStoreModel(): SeeRangeInStore | undefined {
        return this.seeRangeInStoreService.getSeeRangeInStoreModel(this.id);
    }
    loadSeeRangeInStoreModel(): Promise<void> {
        return new Promise<void>(resolve => {
            this.seeRangeInStoreService.loadSeeRangeInStoreModel(this.id).finally(() => {
                const storeId = this.seeRangeInStoreModel?.storepickupId;
                if (storeId) {
                    this.storeResource.load(storeId, 3).finally(resolve);
                } else {
                    resolve();
                }
            });
        });
    }
    get clearanceParent(): Product<IProductDataV2> | undefined {
        if (this.isClearance && this.clearanceParentSku) {
            return this.resources.product.getAllExisting().find(i => i.data && i.data.sku === this.clearanceParentSku) as
                | Product<IProductDataV2>
                | undefined;
        }
        return undefined;
    }
    private get clearanceParentSku(): string | undefined {
        if (this.isClearance) {
            const sku = this.data?.sku;
            if (sku && sku.indexOf('-') > 0) {
                const parts = sku.split('-');
                parts.pop();
                return parts.join('-');
            }
        }
        return undefined;
    }
    get rangeCode(): string | number | undefined {
        return this.getAttrLabel('nyw_item_range_code_nh');
    }
    get brandCode(): string | number | undefined {
        return this.getAttrLabel('nyw_item_brand_code_nh');
    }
    get displayRangeCode(): boolean {
        const rangeCode = this.rangeCode;
        if (rangeCode === 'ACCESSORIES' || rangeCode === 'RUGS') {
            return false;
        }
        return !!rangeCode;
    }
    get displayBrandCode(): boolean {
        const brandCode = this.brandCode;
        if (brandCode === 'BARKER') {
            return false;
        }
        return !!brandCode;
    }
    private getAttrLabel(code: string): string | number | undefined {
        const attrValue = this.data?.[code];
        if (attrValue) {
            return optionLabelPipe(attrValue, code);
        }
        return undefined;
    }
    get canShowMoreColourOptionsAvailableInfo(): boolean {
        return (<IProductDataV2>this.data)?.show_more_colour_options || (<IProductDataV2>this.parent?.data)?.show_more_colour_options;
    }
    override get parent(): Product {
        return <Product>super.parent;
    }
    get canDisplayReviews(): boolean {
        const data = <IProductDataV3>this.data;
        return this.app.client.isCms || !!(data.reviews?.count && data.reviews?.rating >= 80);
    }
    get reviewsRating(): number {
        return ((<IProductDataV3>this.data).reviews?.rating || 0) / 20;
    }
    get variantAttribute1(): string | undefined {
        return <string>optionLabelPipe((<IProductDataV2>this.data).variant_attribute_1, 'variant_attribute_1')
            ?.toString()
            .split('|')
            .pop();
    }

    get displayQtySelector(): boolean {
        const data = this.data as IProductDataV3
        if (this.data) {
            return (
                data.storedepartmentcode === EProductStoreDepartmentCode.accessories ||
                data.nyw_item_category_code_nh === ENywItemCategoryCodeNh.diningChairs ||
                data.nyw_item_category_code_nh === ENywItemCategoryCodeNh.barStools
            );
        }
        return false;
    }
}
@Injectable({
    providedIn: 'root'
})
export class LocalProductResource extends Resource<Product, number> {
    static override instance = Product;
    readonly invalidatesUniversal = true;
    readonly invalidatesPrerender = true;
}
